import {type AttachmentFile} from '@hconnect/common/types'
import {CardSectionTitle} from '@hconnect/uikit/src/lib2'
import {Skeleton} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {COMMON_ATTACHMENTS_WIDGET_NAMESPACE} from '../consts'
import {type AttachementUploadConfig} from '../types'

import {AttachmentUpload} from './AttachmentUpload'

const MAX_ALLOWED_ATTACHMENTS = 10
const FILE_MAX_SIZE_IN_MB = 10

interface InlineAttachmentsUploadProps {
  attachments: AttachmentFile[]
  setAttachments: (attachments: AttachmentFile[]) => void
  isAddingEnabled?: boolean
  isDeletingEnabled?: boolean
  isDisabled?: boolean
  isLoading?: boolean
}

export const InlineAttachmentsUpload: React.FC<InlineAttachmentsUploadProps> = ({
  attachments,
  setAttachments,
  isAddingEnabled = true,
  isDeletingEnabled = true,
  isDisabled = false,
  isLoading = false
}) => {
  const {t} = useTranslation(COMMON_ATTACHMENTS_WIDGET_NAMESPACE)

  const attachmentUploadConfig = useMemo(
    (): AttachementUploadConfig => ({
      allowedFormats: [
        '.pdf',
        '.jpeg',
        '.jpg',
        '.png',
        '.xls',
        '.xlsx',
        '.doc',
        '.docx',
        '.ppt',
        '.pptx'
      ],
      maxAttachements: MAX_ALLOWED_ATTACHMENTS,
      maxFileSizeMB: FILE_MAX_SIZE_IN_MB,
      isAddingEnabled: isAddingEnabled && !isDisabled,
      isDeletingEnabled: isDeletingEnabled && !isDisabled
    }),
    [isAddingEnabled, isDeletingEnabled, isDisabled]
  )

  if (isLoading) {
    return <Skeleton variant="rectangular" sx={{width: 1, height: ({spacing}) => spacing(20)}} />
  }

  return (
    <AttachmentUpload
      attachments={attachments}
      setAttachments={setAttachments}
      attachmentsTitle={
        <CardSectionTitle variant="h4">
          {t('common.attachments.label', {
            amount: attachments.length,
            maxAmount: MAX_ALLOWED_ATTACHMENTS
          })}
        </CardSectionTitle>
      }
      config={attachmentUploadConfig}
    />
  )
}
